import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Divider, Chip, Stack, Button } from '@mui/material';
import { CheckCircle, Cancel, Download as DownloadIcon, Home as HomeIcon } from '@mui/icons-material';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://api.pay.viptecnologia.com.br/api/v1';

const fmtRow = (label: string, value?: string | null) =>
  value ? (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', ml: 2 }}>{value}</Typography>
    </Box>
  ) : null;

const ValidarComprovantePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const h = params.get('h') || '';
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState<boolean | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/external/payment-requests/${id}/validate?h=${encodeURIComponent(h)}`);
        const j = await r.json();
        setValid(!!j.valid);
        setData(j.data || null);
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, h]);

  // Libera o scroll na raiz — o app-shell trava html/#root (overflow:hidden / 100vh)
  // p/ o dashboard; aqui a página é pública e pode passar da altura da tela.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow, htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow, bodyHeight: body.style.height, bodyPosition: body.style.position,
      rootHeight: root?.style.height, rootOverflow: root?.style.overflow, rootMinH: root?.style.minHeight,
    };
    html.style.overflow = 'visible'; html.style.height = 'auto';
    body.style.overflow = 'visible'; body.style.overflowX = 'hidden'; body.style.height = 'auto'; body.style.position = 'static';
    if (root) { root.style.height = 'auto'; root.style.minHeight = '100vh'; root.style.overflow = 'visible'; }
    return () => {
      html.style.overflow = prev.htmlOverflow; html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow; body.style.height = prev.bodyHeight; body.style.position = prev.bodyPosition;
      if (root) { root.style.height = prev.rootHeight || ''; root.style.minHeight = prev.rootMinH || ''; root.style.overflow = prev.rootOverflow || ''; }
    };
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 380, width: '100%', borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Chip label="Sicoob" size="small" sx={{ bgcolor: '#00AE9D', color: '#fff', fontWeight: 700, mb: 2 }} />
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : valid ? (
          <>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CheckCircle sx={{ fontSize: 56, color: '#0a8f3c' }} />
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>Comprovante autêntico</Typography>
              <Typography variant="body2" color="text.secondary">Validado pelo FalaVipPay</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ textAlign: 'center', my: 2 }}>{data?.valorFormatado}</Typography>
            <Divider sx={{ my: 1.5 }} />
            {fmtRow('Data', data?.dataFormatada)}
            {fmtRow('Forma', data?.tipoLabel)}
            {fmtRow('Favorecido', data?.favorecido?.nome)}
            {fmtRow('CPF/CNPJ', data?.favorecido?.documento)}
            {fmtRow('Chave PIX', data?.favorecido?.chave)}
            {fmtRow('Banco', data?.favorecido?.contaBanco)}
            {fmtRow('Descrição', data?.descricao)}
            {data?.endToEndId && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">ID da transação (E2E)</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>{data.endToEndId}</Typography>
              </Box>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Autenticação</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#00AE9D', fontWeight: 700 }}>{data?.hash}</Typography>
            </Stack>
            {data?.pdf_url && (
              <Button
                fullWidth variant="contained" startIcon={<DownloadIcon />}
                href={data.pdf_url} target="_blank" rel="noopener"
                sx={{ mt: 2.5, bgcolor: '#00AE9D', '&:hover': { bgcolor: '#009488' }, textTransform: 'none', fontWeight: 700 }}
              >
                Baixar PDF do comprovante
              </Button>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Cancel sx={{ fontSize: 56, color: '#dc2626' }} />
            <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>Comprovante inválido</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Não foi possível validar este comprovante. O código pode estar incorreto ou o pagamento não foi confirmado.
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.2 }}>
            Pagamentos, PIX e boletos integrados ao Sicoob.
          </Typography>
          <Button
            fullWidth variant="outlined" startIcon={<HomeIcon />} href="/"
            sx={{
              borderColor: '#00AE9D', color: '#00AE9D', textTransform: 'none', fontWeight: 700,
              '&:hover': { borderColor: '#009488', bgcolor: 'rgba(0,174,157,0.06)' },
            }}
          >
            Conhecer o FalaVipPay
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          FalaVipPay • validação de comprovantes
        </Typography>
      </Paper>
    </Box>
  );
};

export default ValidarComprovantePage;
